import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

const SectionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'auto',
  minHeight: '548px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '40px',
  width: '100%',
  padding: '80px 24px',
  backgroundColor: '#0f172a', // slate-900
  [theme.breakpoints.up('md')]: {
    padding: '128px 48px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '128px 64px'
  },
  [theme.breakpoints.down('md')]: {
    gap: '30px',
    minHeight: 'auto'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '24px'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  gap: '6px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 800,
  fontSize: '46px',
  textAlign: 'center',
  letterSpacing: '-0.025em',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  '& .black': {
    color: '#ffffff'
  },
  '& .blue': {
    background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '34px',
    whiteSpace: 'normal'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px'
  }
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  color: '#94a3b8', // slate-400
  textAlign: 'center',
  letterSpacing: '0',
  lineHeight: 1.6,
  maxWidth: '844px',
  padding: '0 20px',
  [theme.breakpoints.down('md')]: {
    fontSize: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px'
  }
}));

const BenefitsGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '22px',
  width: '100%'
}));

const BenefitRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '48px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    gap: '24px',
    flexDirection: 'column'
  }
}));

const BenefitItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '564px',
  maxWidth: '100%',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 20px',
  backgroundColor: '#1e293b', // slate-800
  border: '1px solid #334155', // slate-700
  borderRadius: '8px',
  transition: 'border-color 0.3s ease, transform 0.3s ease',
  '&:hover': {
    borderColor: '#22d3ee',
    transform: 'translateX(4px)'
  },
  [theme.breakpoints.down('md')]: {
    width: '100%'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px 16px',
    gap: '8px'
  }
}));

const CheckButton = styled(Button)(({ theme }) => ({
  width: '180px',
  height: '48px',
  borderRadius: '27px',
  boxShadow: '0px 10px 30px rgba(212, 212, 212, 0.3)',
  background: 'linear-gradient(119deg, rgba(11, 145, 214, 1) 0%, rgba(239, 35, 42, 1) 100%)',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  color: '#ffffff',
  textAlign: 'center',
  letterSpacing: '0',
  lineHeight: '16px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(119deg, rgba(11, 145, 214, 0.9) 0%, rgba(239, 35, 42, 0.9) 100%)',
    boxShadow: '0px 12px 35px rgba(212, 212, 212, 0.4)'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '240px',
    height: '44px',
    fontSize: '14px'
  }
}));

const DIYBenefitsSection = () => {
  const navigate = useNavigate();

  const handleCheckNowClick = () => {
    navigate('/login');
  };

  const benefits = [
    [
      'Transparent & unique scoring system (like CIBIL Score of Local SEO)',
      'Save 90% agency cost – DIY with AI support'
    ],
    [
      'Clear performance insights (no jargon, just numbers & actions)',
      'Help you to Scalable from single store → national franchise'
    ],
    [
      'Trust & Control – No dependency on external SEO agencies',
      'Future-Proof with AI – Stay ahead in rankings with daily tasks'
    ]
  ];

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>
          <span className="black">DIY ( Do-it-Yourself) Growth </span>
          <span className="blue">Platform Benefits</span>
        </SectionTitle>
        <SectionSubtitle>
          A results-driven platform that puts business growth in your hands—simple, effective, and independent.
        </SectionSubtitle>
      </SectionHeader>

      <BenefitsGrid>
        {benefits.map((row, rowIndex) => (
          <BenefitRow key={rowIndex}>
            {row.map((benefit, itemIndex) => (
              <BenefitItem key={itemIndex}>
                <img
                  style={{ width: '28px', height: '28px' }}
                  alt="Check"
                  src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/ic-outline-add-task.svg"
                />
                <Typography sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#ffffff',
                  letterSpacing: '0',
                  lineHeight: '22px',
                  flex: 1
                }}>
                  {benefit}
                </Typography>
              </BenefitItem>
            ))}
          </BenefitRow>
        ))}
      </BenefitsGrid>

      <CheckButton onClick={handleCheckNowClick}>
        Check Now
      </CheckButton>
    </SectionContainer>
  );
};

export default DIYBenefitsSection;
